import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { toast } from 'react-hot-toast';

const CategoryModal = ({ 
  isOpen, 
  onClose, 
  mode, 
  category, 
  parentCategory, 
  onSave,
  categories = []
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    parentId: null
  });
  const [subcategories, setSubcategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'edit' && category) {
      setFormData({
        name: category?.name || '',
        description: category?.description || '',
        status: category?.status || 'active',
        parentId: category?.parentId || null
      });
      setSubcategories(category?.subcategories || []);
    } else if (mode === 'subcategory' && parentCategory) {
      setFormData({
        name: '',
        description: '',
        status: 'active',
        parentId: parentCategory?.id
      });
      setSubcategories([]);
    } else {
      setFormData({
        name: '',
        description: '',
        status: 'active',
        parentId: null
      });
      setSubcategories([]);
    }
    setErrors({});
  }, [isOpen, mode, category, parentCategory]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Category name is required';
    } else if (formData?.name?.length < 2) {
      newErrors.name = 'Category name must be at least 2 characters';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData?.description?.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    // Check for duplicate names
    const existingNames = categories?.filter(cat => 
      cat?.id !== category?.id && 
      cat?.name?.toLowerCase() === formData?.name?.toLowerCase()?.trim()
    );
    if (existingNames?.length > 0) {
      newErrors.name = 'Category name already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const categoryData = {
        ...formData,
        name: formData?.name?.trim(),
        description: formData?.description?.trim(),
        subcategories: mode === 'edit' ? subcategories : []
      };

      await onSave(categoryData, mode);
      onClose();
      toast?.success(`Category ${mode === 'edit' ? 'updated' : 'created'} successfully`);
    } catch (error) {
      toast?.error('Failed to save category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubcategory = () => {
    const name = prompt('Enter subcategory name:')?.trim();
    if (!name) return;
    
    if (subcategories?.some(sub => sub?.name?.toLowerCase() === name?.toLowerCase())) {
      toast?.error('Subcategory name already exists');
      return;
    }

    const newSubcategory = {
      id: Date.now(),
      name,
      status: 'active',
      parentId: category?.id || Date.now() - 1
    };

    setSubcategories(prev => [...prev, newSubcategory]);
  };

  const handleRemoveSubcategory = (subcategoryId) => {
    setSubcategories(prev => prev?.filter(sub => sub?.id !== subcategoryId));
  };

  const handleToggleSubcategoryStatus = (subcategoryId) => {
    setSubcategories(prev => prev?.map(sub => 
      sub?.id === subcategoryId 
        ? { ...sub, status: sub?.status === 'active' ? 'inactive' : 'active' }
        : sub
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {mode === 'create' && 'Create New Category'}
              {mode === 'edit' && 'Edit Category'}
              {mode === 'subcategory' && `Add Subcategory to "${parentCategory?.name}"`}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === 'subcategory' ?'Create a subcategory for better organization' :'Fill in the category details below'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Category Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category Name *
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors?.name ? 'border-red-500' : 'border-border'
                }`}
                placeholder="Enter category name"
                disabled={loading}
              />
              {errors?.name && (
                <p className="text-red-500 text-sm mt-1">{errors?.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description *
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors?.description ? 'border-red-500' : 'border-border'
                }`}
                placeholder="Enter category description"
                disabled={loading}
              />
              {errors?.description && (
                <p className="text-red-500 text-sm mt-1">{errors?.description}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={formData?.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={loading}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Subcategories Section - Only for main categories */}
            {mode !== 'subcategory' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-foreground">
                    Subcategories ({subcategories?.length})
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSubcategory}
                    className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                    disabled={loading}
                  >
                    <Icon name="Plus" size={14} className="inline mr-1" />
                    Add Subcategory
                  </button>
                </div>
                
                {subcategories?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-border rounded-lg p-3">
                    {subcategories?.map((subcategory) => (
                      <div key={subcategory?.id} className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded border">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-foreground">
                            {subcategory?.name}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            subcategory?.status === 'active' ?'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                          }`}>
                            {subcategory?.status}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            type="button"
                            onClick={() => handleToggleSubcategoryStatus(subcategory?.id)}
                            className="p-1 hover:bg-muted rounded transition-colors"
                            title={`${subcategory?.status === 'active' ? 'Deactivate' : 'Activate'} subcategory`}
                          >
                            <Icon 
                              name={subcategory?.status === 'active' ? "EyeOff" : "Eye"} 
                              size={14} 
                              className="text-muted-foreground" 
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveSubcategory(subcategory?.id)}
                            className="p-1 hover:bg-destructive/10 rounded transition-colors"
                            title="Remove subcategory"
                          >
                            <Icon name="Trash2" size={14} className="text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                    No subcategories added yet. Click "Add Subcategory" to create one.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
              disabled={loading}
            >
              {loading && <Icon name="Loader2" size={16} className="animate-spin" />}
              <span>{loading ? 'Saving...' : 'Save Category'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryModal;